import { useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook to automatically load more content when the container
 * doesn't have enough content to scroll (content height <= viewport height)
 *
 * @param {Object} options - Configuration options
 * @param {React.RefObject} options.listRef - Ref to SmartListView component
 * @param {Array} options.data - Current data array
 * @param {boolean} options.hasMore - Whether there's more data to load
 * @param {Function} options.loadMore - Function to load more data
 * @param {boolean} options.loading - Whether data is currently loading
 * @param {number} options.checkDelay - Delay in ms before checking (default: 100)
 * @param {number} options.debounceMs - Debounce delay for resize/resize checks (default: 300)
 */
export const useAutoLoadMore = ({ listRef, data, hasMore, loadMore, loading, checkDelay = 100, debounceMs = 300 }) => {
   const checkTimeoutRef = useRef(null);
   const resizeObserverRef = useRef(null);
   const lastDataLengthRef = useRef(0);

   const checkAndLoadMore = useCallback(() => {
      if (loading || !hasMore || !listRef?.current) {
         return;
      }

      try {
         const container = listRef.current.getScrollContainer?.();
         if (!container) {
            return;
         }

         const { scrollHeight, clientHeight } = container;

         // If content doesn't fill the viewport and we have more data, load more
         if (scrollHeight <= clientHeight && hasMore && !loading) {
            loadMore();
         }
      } catch (error) {
         console.warn('Error checking auto-load more:', error);
      }
   }, [loading, hasMore, listRef, loadMore]);

   // Debounced check function
   const debouncedCheck = useCallback(() => {
      if (checkTimeoutRef.current) {
         clearTimeout(checkTimeoutRef.current);
      }

      checkTimeoutRef.current = setTimeout(() => {
         checkAndLoadMore();
      }, checkDelay);
   }, [checkAndLoadMore, checkDelay]);

   // Check when data changes
   useEffect(() => {
      // Only check if data length changed (new items loaded)
      if (data.length !== lastDataLengthRef.current) {
         lastDataLengthRef.current = data.length;
         debouncedCheck();
      }

      return () => {
         if (checkTimeoutRef.current) {
            clearTimeout(checkTimeoutRef.current);
         }
      };
   }, [data.length, debouncedCheck]);

   // Check on mount and when listRef becomes available
   useEffect(() => {
      if (listRef?.current) {
         debouncedCheck();
      }
   }, [listRef, debouncedCheck]);

   // Handle window resize to check if we need to load more after resolution change
   useEffect(() => {
      let resizeTimer = null;

      const handleResize = () => {
         if (resizeTimer) {
            clearTimeout(resizeTimer);
         }
         resizeTimer = setTimeout(() => {
            debouncedCheck();
         }, debounceMs);
      };

      window.addEventListener('resize', handleResize);

      return () => {
         window.removeEventListener('resize', handleResize);
         if (resizeTimer) {
            clearTimeout(resizeTimer);
         }
      };
   }, [debounceMs, debouncedCheck]);

   // Setup ResizeObserver for container size changes (separate effect to handle container availability)
   useEffect(() => {
      if (!listRef?.current) {
         return;
      }

      try {
         const container = listRef.current.getScrollContainer?.();
         if (!container) {
            return;
         }

         // Disconnect existing observer if any
         if (resizeObserverRef.current) {
            resizeObserverRef.current.disconnect();
         }

         resizeObserverRef.current = new ResizeObserver(() => {
            debouncedCheck();
         });
         resizeObserverRef.current.observe(container);

         return () => {
            if (resizeObserverRef.current) {
               resizeObserverRef.current.disconnect();
            }
         };
      } catch (error) {
         // ResizeObserver not supported, fallback to window resize only
         console.warn('ResizeObserver not supported:', error);
      }
   }, [listRef, debouncedCheck]);

   return { checkAndLoadMore };
};
